import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Divider,
  Grid,
  GridItem,
} from '@chakra-ui/react';

import type { LocationCoverages } from 'api/quote-marketplace';
import { createEmptyLocationCoverages } from '../constants/habitationalCoveragesOptions';
import { useAnswerCodes, type SumpPumpLocation } from '../hooks';
import {
  CoverageBasicsSection,
  HomeBasedBusinessSection,
  LegalCoveragesSection,
  WaterDamagesSection,
  SwimmingPoolSection,
  PropertyInStorageSection,
  AdditionalCoveragesSection,
} from './coverages-modal';

interface AllPropertyCoveragesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (coverages: LocationCoverages) => void;
  initialCoverages: LocationCoverages;
  location: SumpPumpLocation;
  isUpdating?: boolean;
  quoteId: number;
  province: number;
}

const AllPropertyCoveragesModal = ({
  isOpen,
  onClose,
  onSave,
  initialCoverages,
  location,
  isUpdating = false,
  quoteId,
  province,
}: AllPropertyCoveragesModalProps) => {
  const { isLoading: isLoadingCodes } = useAnswerCodes();

  const [coverages, setCoverages] = useState<LocationCoverages>(
    initialCoverages || createEmptyLocationCoverages(),
  );

  useEffect(() => {
    if (isOpen) {
      setCoverages(initialCoverages || createEmptyLocationCoverages());
    }
  }, [isOpen, initialCoverages]);

  const updateCoverage = useCallback(
    <K extends keyof LocationCoverages>(
      key: K,
      value: LocationCoverages[K],
    ) => {
      setCoverages((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleApply = useCallback(() => {
    onSave(coverages);
    onClose();
  }, [coverages, onSave, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="5xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader fontWeight="600" pb={2}>
          All Coverages
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <CoverageBasicsSection
              coverages={coverages}
              updateCoverage={updateCoverage}
              isLoadingCodes={isLoadingCodes}
              quoteId={quoteId}
              province={province}
            />

            <Divider />

            <WaterDamagesSection
              coverages={coverages}
              updateCoverage={updateCoverage}
              location={location}
            />

            <Divider />

            <HomeBasedBusinessSection
              coverages={coverages}
              updateCoverage={updateCoverage}
            />

            <Divider />

            <LegalCoveragesSection
              coverages={coverages}
              updateCoverage={updateCoverage}
            />

            <Divider />

            <Grid templateColumns="1fr 1fr" gap={4}>
              <GridItem>
                <SwimmingPoolSection
                  coverages={coverages}
                  updateCoverage={updateCoverage}
                  isLoadingCodes={isLoadingCodes}
                />
              </GridItem>
              <GridItem>
                <PropertyInStorageSection
                  coverages={coverages}
                  updateCoverage={updateCoverage}
                  isLoadingCodes={isLoadingCodes}
                />
              </GridItem>
            </Grid>

            <Divider />

            <AdditionalCoveragesSection
              coverages={coverages}
              updateCoverage={updateCoverage}
              isLoadingCodes={isLoadingCodes}
            />
          </VStack>
        </ModalBody>

        <ModalFooter gap={2} pt={4}>
          <Button
            variant="outline"
            onClick={handleCancel}
            isDisabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            colorScheme="yellow"
            onClick={handleApply}
            isLoading={isUpdating}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AllPropertyCoveragesModal;
